import { useEffect } from 'react';
import { withAsyncBoundary } from '@toss/async-boundary';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { convertTimeToUTC, fDateTimeByLanguage } from '@zep/utils';
import { CreditCardList } from '../../../consts';
import { useBusinessPaymentResult } from '../../../services';
import { BusinessPayment } from '../../../types/PaymentTypes';
import PaymentInformation from './components/PaymentInformation';
import { BusinessPaymentLayout } from './BusinessPaymentLayout';
import { BusinessPaymentInvalidPage } from './BusinessPayments';
import S from './BusinessPayments.module.scss';
const BusinessPaymentsSuccessContent = () => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['business']);
  const paymentHashId = (router.query.id ?? '').toString();
  const email = (router.query.email ?? '').toString();
  const {
    data: {
      businessPayment
    }
  } = useBusinessPaymentResult({
    paymentHashId,
    email
  });
  const paymentTime = convertTimeToUTC(businessPayment.paymentApprovedAt);
  const productName = businessPayment.productType === 0 ? t('business:businessPage.productType.normal') : t('business:businessPage.productType.premium');
  useEffect(() => {
    logger.track('payment_business_complete', {
      product_type: businessPayment.productType === 1 ? 'premium_plan' : 'etc',
      currency: businessPayment.currency,
      // revenue는 persisted 실결제액(finalPrice, VAT 포함) 기준.
      // price(VAT 전)는 서버 VAT 합류 전 과도기 fallback이다.
      $revenue: (businessPayment.finalPrice ?? businessPayment.price) * (businessPayment?.currency === 'KRW' ? 1 : 1200)
    });
  }, [businessPayment]);
  return <BusinessPaymentLayout isCenter={false} title={businessPayment.customerName} data-sentry-element="BusinessPaymentLayout" data-sentry-component="BusinessPaymentsSuccessContent" data-sentry-source-file="BusinessPaymentsSuccess.tsx">
      <div className={S.payments_result_section}>
        <h2 className={S.payments_result_title}>
          {t('business:business_payments_complete')}
        </h2>
        <div>
          {t('business:business_payments_complete_content', {
          name: businessPayment.customerName
        })}
        </div>
        <div>
          {t('business:business_payments_complete_confirmation_number')}
          &nbsp;: <span>{businessPayment.orderId}</span>
        </div>
        <div className={S.payments_result_label}>
          {businessPayment?.currency === 'KRW' ? <ResultKRWLabel paymentTime={paymentTime} payment={{
          ...businessPayment
        }} /> : <ResultUSDLabel paymentTime={paymentTime} payment={businessPayment} />}
        </div>
      </div>
      <PaymentInformation currency={businessPayment.currency} businessName={businessPayment.customerName} content={businessPayment.productDetail} productName={productName} price={businessPayment.priceBeforeVat ?? businessPayment.price} vatAmount={businessPayment.vatAmount} data-sentry-element="PaymentInformation" data-sentry-source-file="BusinessPaymentsSuccess.tsx" />
    </BusinessPaymentLayout>;
};
const ResultKRWLabel = ({
  payment,
  paymentTime
}: {
  payment: BusinessPayment;
  paymentTime: Date;
}) => {
  const {
    i18n
  } = useTranslation();
  return <>
      {payment.paymentMethod}
      {fDateTimeByLanguage(paymentTime, i18n.resolvedLanguage ?? 'en')}|{' '}
      {CreditCardList[payment.cardCompanyCode ?? '']}
    </>;
};
const ResultUSDLabel = ({
  paymentTime
}: {
  payment: BusinessPayment;
  paymentTime: Date;
}) => {
  const {
    i18n
  } = useTranslation();
  return <>{fDateTimeByLanguage(paymentTime, i18n.resolvedLanguage ?? 'en')}</>;
};
export const BusinessPaymentsSuccess = withAsyncBoundary(BusinessPaymentsSuccessContent, {
  pendingFallback: '',
  rejectedFallback: () => {
    return <BusinessPaymentInvalidPage />;
  }
});